import { ROOT_NUMBER } from './hierarchy-route';

/*
 * Shared team-hierarchy walk. The dashboard resolves the SLED team tree in two
 * places — the standings scope (lib/scope.ts, over SledTeam) and the rollup
 * aggregation (lib/rollup.ts, over FlatTeam). Both need the same phantom rule and
 * descendant/child-group resolution, so it lives here once rather than as two
 * copies that can drift. The functions type against the minimal structural shape
 * both callers supply.
 */

/** Minimal structural shape needed to walk the hierarchy — satisfied by both
 *  FlatTeam (rollup) and SledTeam (scope). */
export interface HierarchyTeam {
  number: string;
  name: string;
  tier: number;
  parentNumber?: string;
}

export interface TeamLeaf {
  number: string;
  name: string;
}

/** Minimal flat-team shape from getAllTeams used to walk the hierarchy. */
export interface FlatTeam {
  number: string;
  name: string;
  tier: number;
  tierLabel?: string;
  parentNumber?: string;
}

/**
 * One immediate child of the rollup node paired with the leaf scrum teams beneath
 * it. The leaderboard aggregates each group into a single ranked row.
 */
export interface ChildGroup {
  unit: { number: string; name: string; tier: number };
  leaves: TeamLeaf[];
}

/**
 * A phantom tier-4 team duplicates its parent Release Train's name and is hidden
 * everywhere in the dashboard: the standings scope drops it and the rollups
 * exclude it, so their counts and leaderboards agree — otherwise a row whose name
 * duplicates the train would appear.
 */
export function isPhantom<T extends HierarchyTeam>(team: T, allTeams: T[]): boolean {
  if (team.tier !== 4) return false;
  const parent = allTeams.find((t) => t.tier === 3 && t.number === team.parentNumber);
  return !!parent && team.name.trim().toLowerCase() === parent.name.trim().toLowerCase();
}

/** Memoized phantom checker (O(1) parent lookup) for the recursive descendant
 *  walk, where the plain `isPhantom` scan would be called once per candidate. */
function buildPhantomChecker<T extends HierarchyTeam>(allTeams: T[]): (team: T) => boolean {
  const byNumber = new Map<string, T>();
  for (const t of allTeams) byNumber.set(t.number, t);
  return (team: T): boolean => {
    if (team.tier !== 4 || !team.parentNumber) return false;
    const parent = byNumber.get(team.parentNumber);
    return (
      !!parent &&
      parent.tier === 3 &&
      team.name.trim().toLowerCase() === parent.name.trim().toLowerCase()
    );
  };
}

/**
 * Walk the flat team list down from `nodeNumber`, collecting tier-4 team leaves.
 * Works for any non-leaf tier (portfolio → delivery stream → release train → teams).
 */
export function resolveDescendantTeams(allTeams: FlatTeam[], nodeNumber: string): TeamLeaf[] {
  const childrenByParent = new Map<string, FlatTeam[]>();
  for (const t of allTeams) {
    if (!t.parentNumber) continue;
    const arr = childrenByParent.get(t.parentNumber) ?? [];
    arr.push(t);
    childrenByParent.set(t.parentNumber, arr);
  }

  const isPhantomTeam = buildPhantomChecker(allTeams);
  const leaves: TeamLeaf[] = [];
  // The synthetic "Cox Automotive" root has no real number for the parentNumber
  // walk; seed from every tier-1 portfolio so it rolls up the whole company.
  const stack: string[] =
    nodeNumber === ROOT_NUMBER
      ? allTeams.filter((t) => t.tier === 1).map((t) => t.number)
      : [nodeNumber];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const child of childrenByParent.get(cur) ?? []) {
      if (child.tier >= 4) {
        if (!isPhantomTeam(child)) leaves.push({ number: child.number, name: child.name });
      } else {
        stack.push(child.number);
      }
    }
  }
  return leaves;
}

/**
 * Resolve the immediate children of `nodeNumber` (one tier down) into leaderboard
 * groups — each child paired with the leaf scrum teams beneath it. A Portfolio
 * yields its Delivery Streams; a Stream yields its Release Trains; a Release Train
 * yields its scrum teams (each its own group). Phantom and childless groups are
 * dropped so the leaderboard mirrors the standings list.
 */
export function resolveChildGroups(allTeams: FlatTeam[], nodeNumber: string): ChildGroup[] {
  const isPhantomTeam = buildPhantomChecker(allTeams);
  // Root's immediate children are the tier-1 portfolios (its leaderboard units).
  const immediate = (
    nodeNumber === ROOT_NUMBER
      ? allTeams.filter((t) => t.tier === 1)
      : allTeams.filter((t) => t.parentNumber === nodeNumber)
  ).sort((a, b) => a.name.localeCompare(b.name));

  const groups: ChildGroup[] = [];
  for (const child of immediate) {
    let leaves: TeamLeaf[];
    if (child.tier >= 4) {
      if (isPhantomTeam(child)) continue;
      leaves = [{ number: child.number, name: child.name }];
    } else {
      leaves = resolveDescendantTeams(allTeams, child.number);
    }
    if (leaves.length === 0) continue;
    groups.push({ unit: { number: child.number, name: child.name, tier: child.tier }, leaves });
  }
  return groups;
}
